import React, { useState, useEffect, useCallback } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useAdminScope } from '../hooks/useAdminScope'
import { supabaseClient } from '../config/supabase'
import {
  LayoutDashboard,
  BarChart3,
  Users,
  FileText,
  Briefcase,
  Newspaper,
  GraduationCap,
  Target,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  ClipboardList,
  LogOut,
  User,
  Settings,
  MessageCircle,
  MessageSquare,
  Building2,
  Search,
  HeartHandshake,
  CheckCircle2,
  Send,
  TrendingUp,
  ArrowLeftRight,
  Lightbulb,
} from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────
interface NavItemDef {
  icon: React.ElementType
  label: string
  to: string
  disabled?: boolean
  subItem?: boolean
  badge?: number
}

// ─── Nav Config ───────────────────────────────────────────────────────────────
const ADMIN_MAIN: NavItemDef[] = [
  { icon: LayoutDashboard, label: 'Dashboard', to: '/admin' },
]

const ADMIN_ANALYTICS_TABS: NavItemDef[] = [
  { icon: Target,         label: 'PEO/PO Alignment', to: '/admin/analytics?tab=peo' },
  { icon: TrendingUp,     label: 'Tracer Study',      to: '/admin/analytics?tab=tracer' },
  { icon: ArrowLeftRight, label: 'Comparative',       to: '/admin/analytics?tab=comparative' },
  { icon: Lightbulb,      label: 'Insights',          to: '/admin/analytics?tab=interventions' },
]

const ADMIN_MANAGE_SUPER: NavItemDef[] = [
  { icon: Users, label: 'Alumni', to: '/admin/alumni' },
  { icon: Building2, label: 'Employers', to: '/admin/employers' },
  { icon: FileText, label: 'Surveys', to: '/admin/surveys' },
  { icon: Briefcase, label: 'Job Postings', to: '/admin/jobs' },
  { icon: Newspaper, label: 'News & Updates', to: '/admin/news' },
  { icon: GraduationCap, label: 'Programs & PEOs', to: '/admin/programs' },
]

const ADMIN_MANAGE_DEPT: NavItemDef[] = [
  { icon: Users, label: 'Alumni', to: '/admin/alumni' },
  { icon: Building2, label: 'Employers', to: '/admin/employers' },
  { icon: FileText, label: 'Surveys', to: '/admin/surveys' },
  { icon: Newspaper, label: 'News & Updates', to: '/admin/news' },
  { icon: GraduationCap, label: 'Programs & PEOs', to: '/admin/programs' },
]

const ADMIN_TOOLS: NavItemDef[] = [
  { icon: Target, label: 'Reports', to: '/admin/reports' },
]


const ALUMNI_MAIN: NavItemDef[] = [
  { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard' },
]

const ALUMNI_TOOLS: NavItemDef[] = [
  { icon: ClipboardList, label: 'Complete Surveys', to: '/survey' },
  { icon: MessageCircle, label: 'Batch Chat', to: '/batch-chat' },
  { icon: MessageSquare, label: 'Provide Feedback', to: '/provide-feedback' },
]

const ALUMNI_JOBS: NavItemDef[] = [
  { icon: Search, label: 'Browse Jobs', to: '/jobs' },
  { icon: CheckCircle2, label: 'My Applications', to: '/my-applications?tab=applications' },
  { icon: Send, label: 'Invitations', to: '/my-applications?tab=invitations' },
]

// ─── Employer Nav ─────────────────────────────────────────────────────────────
const EMPLOYER_MAIN: NavItemDef[] = [
  { icon: LayoutDashboard, label: 'Dashboard', to: '/employer-dashboard' },
]

const EMPLOYER_JOBS: NavItemDef[] = [
  { icon: Briefcase, label: 'Job Listings', to: '/employer/jobs' },
  { icon: Users, label: 'All Applications', to: '/employer/applications' },
  { icon: Send, label: 'Invitations Sent', to: '/employer/invitations' },
]

const EMPLOYER_TOOLS: NavItemDef[] = [
  { icon: Search, label: 'Find Candidates', to: '/employer/candidates' },
  { icon: HeartHandshake, label: 'Feedback & Support', to: '/employer/feedback' },
]

// ─── NavItem ──────────────────────────────────────────────────────────────────
interface NavItemProps {
  item: NavItemDef
  collapsed: boolean
  active: boolean
  onClick?: () => void
}

const NavItem: React.FC<NavItemProps> = ({ item, collapsed, active, onClick }) => {
  const { icon: Icon, label, to, disabled, subItem, badge } = item
  const isActive = active && !disabled
  const showBadge = !isActive && !disabled && typeof badge === 'number' && badge > 0
  return (
    <div className={`relative group ${subItem && !collapsed ? 'pl-5' : 'px-2'}`}>
      {subItem && !collapsed && (
        <div className="absolute left-7 top-0 bottom-0 w-px bg-gray-200" />
      )}
      <Link
        to={disabled ? '#' : to}
        onClick={disabled ? e => e.preventDefault() : onClick}
        className={`flex items-center gap-3 rounded-lg transition-all duration-150
          ${collapsed ? 'justify-center p-2' : subItem ? 'px-3 py-1.5 ml-2' : 'px-3 py-2'}
          ${isActive
            ? 'bg-blue-50 text-blue-700 font-semibold'
            : disabled
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
      >
        <div className="relative flex-shrink-0">
          <Icon className={`${subItem ? 'w-4 h-4' : 'w-5 h-5'} ${isActive ? 'text-blue-600' : ''}`} />
          {/* Badge for collapsed sidebar: dot on icon */}
          {collapsed && showBadge && (
            <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-0.5 leading-none">
              {badge! > 99 ? '99+' : badge}
            </span>
          )}
        </div>
        {!collapsed && <span className={`${subItem ? 'text-xs' : 'text-sm'} truncate flex-1`}>{label}</span>}
        {!collapsed && disabled && (
          <span className="ml-auto text-[9px] font-bold text-gray-400 uppercase bg-gray-100 px-1.5 py-0.5 rounded">Soon</span>
        )}
        {/* Badge for expanded sidebar: pill on right */}
        {!collapsed && showBadge && (
          <span className="ml-auto min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5">
            {badge! > 99 ? '99+' : badge}
          </span>
        )}
      </Link>
      {/* Tooltip for collapsed mode */}
      {collapsed && (
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 bg-gray-900 text-white
          text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none
          transition-opacity duration-150 z-[60]">
          {label}{showBadge ? ` (${badge} unread)` : ''}
        </span>
      )}
    </div>
  )
}

// ─── Section Label ────────────────────────────────────────────────────────────
const SectionLabel: React.FC<{ label: string; collapsed: boolean }> = ({ label, collapsed }) =>
  collapsed
    ? <div className="my-1 mx-3 border-t border-gray-100" />
    : <p className="px-5 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">{label}</p>

// ─── Sidebar Inner ────────────────────────────────────────────────────────────
interface SidebarInnerProps {
  collapsed: boolean
  isMobile: boolean
  isAdmin: boolean
  isEmployer: boolean
  isSuperAdmin: boolean
  isManageOpen: boolean
  onToggleManage: () => void
  isAnalyticsOpen: boolean
  onToggleAnalytics: () => void
  isJobsOpen: boolean
  onToggleJobs: () => void
  isActiveFn: (to: string) => boolean
  userName?: string
  avatarSrc: string | null
  onNavClick?: () => void
  onLogout: () => void
  chatUnreadCount?: number
  onClose?: () => void
}

const SidebarInner: React.FC<SidebarInnerProps> = ({
  collapsed,
  isMobile,
  isAdmin,
  isEmployer,
  isSuperAdmin,
  isManageOpen,
  onToggleManage,
  isAnalyticsOpen,
  onToggleAnalytics,
  isJobsOpen,
  onToggleJobs,
  isActiveFn,
  userName,
  avatarSrc,
  onNavClick,
  onLogout,
  onClose,
  chatUnreadCount,
}) => {
  const c = collapsed && !isMobile
  const isAnalyticsActive = isActiveFn('/admin/analytics')
  const ADMIN_MANAGE = isSuperAdmin ? ADMIN_MANAGE_SUPER : ADMIN_MANAGE_DEPT

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* ── User card ── */}
      <div className="relative flex flex-col items-center text-center px-3 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
        {/* Mobile close button */}
        {isMobile && onClose && (
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"
            aria-label="Close sidebar"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {/* Avatar */}
        <Link
          to={isAdmin ? '/admin/profile' : isEmployer ? '/employer/profile' : '/profile'}
          onClick={onNavClick}
          title="My Profile"
          className="block mb-2"
        >
          <div className={`rounded-full overflow-hidden bg-blue-900 flex items-center justify-center ring-2 ring-transparent hover:ring-blue-400 transition-all mx-auto
            ${c ? 'w-9 h-9' : 'w-14 h-14'}`}>
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt="avatar"
                className="w-full h-full object-cover"
                onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.svg' }}
              />
            ) : (
              <User className={c ? 'w-4 h-4 text-white' : 'w-7 h-7 text-white'} />
            )}
          </div>
        </Link>
        {/* Name + role (hidden when collapsed) */}
        {!c && (
          <>
            <p className="text-sm font-bold text-gray-900 truncate w-full leading-tight">{userName || 'User'}</p>
            <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide
              ${isAdmin ? 'bg-yellow-400 text-yellow-900' : isEmployer ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
              {isAdmin ? 'Admin' : isEmployer ? 'Employer' : 'Alumni'}
            </span>
          </>
        )}
      </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 py-1 space-y-0 overflow-y-auto min-h-0">
        {isAdmin ? (
          <>
            {/* MAIN */}
            <SectionLabel label="Main" collapsed={c} />
            {ADMIN_MAIN.map(item => (
              <NavItem
                key={item.to}
                item={item}
                collapsed={c}
                active={isActiveFn(item.to)}
                onClick={onNavClick}
              />
            ))}

            {/* ANALYTICS — expandable */}
            {c ? (
              <div className="relative group px-2">
                <button
                  className={`flex items-center justify-center w-full p-2.5 rounded-lg transition-colors
                    ${isAnalyticsActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                  title="Analytics"
                >
                  <BarChart3 className={`w-5 h-5 ${isAnalyticsActive ? 'text-blue-600' : ''}`} />
                </button>
                <div className="absolute left-full top-0 ml-3 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-2
                  opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto
                  transition-opacity duration-150 z-[60]">
                  <p className="px-3 pb-1 pt-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Analytics</p>
                  {ADMIN_ANALYTICS_TABS.map(tab => {
                    const Icon = tab.icon
                    return (
                      <Link
                        key={tab.to}
                        to={tab.to}
                        onClick={onNavClick}
                        className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors
                          ${isActiveFn(tab.to)
                            ? 'text-blue-700 bg-blue-50 font-semibold'
                            : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span>{tab.label}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div className="px-2">
                <button
                  onClick={onToggleAnalytics}
                  className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg transition-colors
                    ${isAnalyticsActive ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                >
                  <BarChart3 className={`w-5 h-5 flex-shrink-0 ${isAnalyticsActive ? 'text-blue-600' : ''}`} />
                  <span className="text-sm flex-1 text-left">Analytics</span>
                  <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isAnalyticsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isAnalyticsOpen && (
                  <div className="mt-0.5 ml-7 space-y-0.5 border-l-2 border-gray-100 pl-1">
                    {ADMIN_ANALYTICS_TABS.map(tab => (
                      <NavItem
                        key={tab.to}
                        item={tab}
                        collapsed={false}
                        active={isActiveFn(tab.to)}
                        onClick={onNavClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* MANAGE */}
            <SectionLabel label="Manage" collapsed={c} />
            {c ? (
              /* Collapsed: flyout on hover */
              <div className="relative group px-2">
                <button
                  className="flex items-center justify-center w-full p-2.5 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  title="Manage"
                >
                  <Settings className="w-5 h-5" />
                </button>
                <div className="absolute left-full top-0 ml-3 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-2
                  opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto
                  transition-opacity duration-150 z-[60]">
                  <p className="px-3 pb-1 pt-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Manage</p>
                  {ADMIN_MANAGE.map(item => {
                    const Icon = item.icon
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors
                          ${isActiveFn(item.to)
                            ? 'text-blue-700 bg-blue-50 font-semibold'
                            : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>
            ) : (
              /* Expanded: accordion */
              <div className="px-2">
                <button
                  onClick={onToggleManage}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <Settings className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm flex-1 text-left">Manage</span>
                  <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isManageOpen ? 'rotate-180' : ''}`} />
                </button>
                {isManageOpen && (
                  <div className="mt-0.5 ml-7 space-y-0.5 border-l-2 border-gray-100 pl-1">
                    {ADMIN_MANAGE.map(item => (
                      <NavItem
                        key={item.to}
                        item={item}
                        collapsed={false}
                        active={isActiveFn(item.to)}
                        onClick={onNavClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TOOLS */}
            <SectionLabel label="Tools" collapsed={c} />
            {ADMIN_TOOLS.map(item => (
              <NavItem
                key={item.to}
                item={item}
                collapsed={c}
                active={isActiveFn(item.to)}
                onClick={onNavClick}
              />
            ))}

          </>
        ) : isEmployer ? (
          <>
            {/* MAIN */}
            <SectionLabel label="Main" collapsed={c} />
            {EMPLOYER_MAIN.map(item => (
              <NavItem
                key={item.to}
                item={item}
                collapsed={c}
                active={isActiveFn(item.to)}
                onClick={onNavClick}
              />
            ))}

            {/* JOBS (dropdown like alumni Jobs) */}
            <SectionLabel label="Job Management" collapsed={c} />
            {c ? (
              /* Collapsed: flyout on hover */
              <div className="relative group px-2">
                <button
                  className="flex items-center justify-center w-full p-2.5 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  title="Job Management"
                >
                  <Briefcase className="w-5 h-5" />
                </button>
                <div className="absolute left-full top-0 ml-3 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-2
                  opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto
                  transition-opacity duration-150 z-[60]">
                  <p className="px-3 pb-1 pt-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Job Management</p>
                  {EMPLOYER_JOBS.map(item => {
                    const Icon = item.icon
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors
                          ${isActiveFn(item.to)
                            ? 'text-blue-700 bg-blue-50 font-semibold'
                            : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>
            ) : (
              /* Expanded: accordion */
              <div className="px-2">
                <button
                  onClick={onToggleJobs}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <Briefcase className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm flex-1 text-left">Job Management</span>
                  <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isJobsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isJobsOpen && (
                  <div className="mt-0.5 ml-7 space-y-0.5 border-l-2 border-gray-100 pl-1">
                    {EMPLOYER_JOBS.map(item => (
                      <NavItem
                        key={item.to}
                        item={item}
                        collapsed={false}
                        active={isActiveFn(item.to)}
                        onClick={onNavClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TOOLS */}
            <SectionLabel label="Employer Tools" collapsed={c} />
            {EMPLOYER_TOOLS.map(item => (
              <NavItem
                key={item.label}
                item={item}
                collapsed={c}
                active={isActiveFn(item.to)}
                onClick={onNavClick}
              />
            ))}
          </>
        ) : (
          <>
            {/* MAIN */}
            <SectionLabel label="Main" collapsed={c} />
            {ALUMNI_MAIN.map(item => (
              <NavItem
                key={item.to}
                item={item}
                collapsed={c}
                active={isActiveFn(item.to)}
                onClick={onNavClick}
              />
            ))}

            {/* JOBS (dropdown like admin Manage) */}
            <SectionLabel label="Jobs" collapsed={c} />
            {c ? (
              /* Collapsed: flyout on hover */
              <div className="relative group px-2">
                <button
                  className="flex items-center justify-center w-full p-2.5 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  title="Jobs"
                >
                  <Briefcase className="w-5 h-5" />
                </button>
                <div className="absolute left-full top-0 ml-3 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-2
                  opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto
                  transition-opacity duration-150 z-[60]">
                  <p className="px-3 pb-1 pt-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Jobs</p>
                  {ALUMNI_JOBS.map(item => {
                    const Icon = item.icon
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors
                          ${isActiveFn(item.to)
                            ? 'text-blue-700 bg-blue-50 font-semibold'
                            : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>
            ) : (
              /* Expanded: accordion */
              <div className="px-2">
                <button
                  onClick={onToggleJobs}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <Briefcase className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm flex-1 text-left">Jobs</span>
                  <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isJobsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isJobsOpen && (
                  <div className="mt-0.5 ml-7 space-y-0.5 border-l-2 border-gray-100 pl-1">
                    {ALUMNI_JOBS.map(item => (
                      <NavItem
                        key={item.label}
                        item={item}
                        collapsed={false}
                        active={isActiveFn(item.to)}
                        onClick={onNavClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TOOLS */}
            <SectionLabel label="Tools" collapsed={c} />
            {ALUMNI_TOOLS.map(item => (
              <NavItem
                key={item.label}
                item={item.to === '/batch-chat' ? { ...item, badge: chatUnreadCount } : item}
                collapsed={c}
                active={isActiveFn(item.to)}
                onClick={onNavClick}
              />
            ))}
          </>
        )}
      </nav>

      {/* ── Logout ── */}
      <div className="border-t border-gray-100 py-1.5 flex-shrink-0 px-2">
        <button
          onClick={onLogout}
          className={`flex items-center gap-3 rounded-lg transition-colors text-gray-500 hover:text-red-600 hover:bg-red-50 w-full
            ${c ? 'justify-center p-2' : 'px-3 py-2'}`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!c && <span className="text-sm">Logout</span>}
        </button>
      </div>
    </div>
  )
}

// ─── Dashboard Layout ─────────────────────────────────────────────────────────
const DashboardLayout: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isManageOpen, setIsManageOpen] = useState(false)
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false)
  const [isJobsOpen, setIsJobsOpen] = useState(false)
  const [chatUnreadCount, setChatUnreadCount] = useState(0)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const isAdmin = user?.role === 'admin'
  const isEmployer = user?.role === 'employer'
  const { isSuperAdmin } = useAdminScope()

  // ─── Fetch total unread chat count (alumni only, skip when on /batch-chat) ──
  const fetchChatUnread = useCallback(async () => {
    if (isAdmin || isEmployer) return
    if (location.pathname === '/batch-chat') {
      setChatUnreadCount(0)
      return
    }
    const { data, error } = await supabaseClient.rpc('get_my_chat_rooms')
    if (error || !data) return
    const total = (data as any[]).reduce((sum: number, r: any) => sum + (r.is_muted ? 0 : (r.unread_count || 0)), 0)
    setChatUnreadCount(total)
  }, [isAdmin, isEmployer, location.pathname])

  useEffect(() => {
    fetchChatUnread()
  }, [fetchChatUnread])

  // Poll every 30 seconds to keep count fresh
  useEffect(() => {
    if (isAdmin || isEmployer) return
    const interval = setInterval(fetchChatUnread, 30_000)
    return () => clearInterval(interval)
  }, [isAdmin, isEmployer, fetchChatUnread])

  const isActiveFn = (to: string): boolean => {
    // Strip any query string from `to` before comparing against pathname
    const toPath = to.split('?')[0]
    const toSearch = to.includes('?') ? to.slice(to.indexOf('?')) : null
    if (toPath === '/admin') return location.pathname === '/admin'
    if (!toSearch) {
      return location.pathname === toPath || location.pathname.startsWith(toPath + '/')
    }
    // For items with a query string, require both pathname AND search to match
    return location.pathname === toPath && location.search === toSearch
  }

  // Auto-expand Manage group when already on a manage sub-route
  useEffect(() => {
    const adminManageItems = isSuperAdmin ? ADMIN_MANAGE_SUPER : ADMIN_MANAGE_DEPT
    if (adminManageItems.some(item => location.pathname.startsWith(item.to.split('?')[0]))) {
      setIsManageOpen(true)
    }
    if (location.pathname.startsWith('/admin/analytics')) {
      setIsAnalyticsOpen(true)
    }
    if (ALUMNI_JOBS.some(item => location.pathname.startsWith(item.to.split('?')[0]))) {
      setIsJobsOpen(true)
    }
    if (EMPLOYER_JOBS.some(item => location.pathname.startsWith(item.to.split('?')[0]))) {
      setIsJobsOpen(true)
    }
  }, [isSuperAdmin])

  // Close mobile drawer on route change
  useEffect(() => {
    setIsMobileOpen(false)
  }, [location.pathname])

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const userName = (() => {
    const p = user?.profile
    if (!p) return user?.email?.split('@')[0]
    const fn = (p as any).first_name
    const ln = (p as any).last_name
    if (fn || ln) return `${fn || ''} ${ln || ''}`.trim()
    const contactPerson = (p as any).contact_person
    if (contactPerson) return contactPerson
    return user?.email?.split('@')[0]
  })()

  const avatarSrc: string | null =
    user?.profile && 'profile_picture' in user.profile
      ? (user.profile as any).profile_picture || null
      : null

  const sharedProps = {
    isAdmin,
    isEmployer,
    isSuperAdmin,
    isManageOpen,
    onToggleManage: () => setIsManageOpen(o => !o),
    isAnalyticsOpen,
    onToggleAnalytics: () => setIsAnalyticsOpen(o => !o),
    isJobsOpen,
    onToggleJobs: () => setIsJobsOpen(o => !o),
    isActiveFn,
    userName,
    avatarSrc,
    onLogout: handleLogout,
    chatUnreadCount,
  }

  return (
    <div className={`flex w-full ${location.pathname === '/batch-chat' ? 'flex-1 min-h-0' : 'flex-1'}`}>
      {/* ── Mobile overlay ──────────────────────────────────────────── */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20 md:hidden"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Desktop sidebar ─────────────────────────────────────────── */}
      <aside
        className={`hidden md:flex flex-col flex-shrink-0 bg-white border-r border-gray-200
          overflow-visible transition-all duration-300
          ${location.pathname === '/batch-chat' ? 'relative h-full' : 'sticky top-0 h-screen'}
          ${isCollapsed ? 'w-[4.5rem]' : 'w-56'}`}
      >
        {/* Overflow wrapper to clip sidebar content but allow toggle button to protrude */}
        <div className="flex flex-col h-full overflow-hidden">
          <SidebarInner {...sharedProps} collapsed={isCollapsed} isMobile={false} />
        </div>

        {/* Collapse/expand toggle button */}
        <button
          onClick={() => setIsCollapsed(c => !c)}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute -right-3 top-[4.5rem] z-10 w-6 h-6 rounded-full bg-blue-900 text-white
            flex items-center justify-center shadow-md hover:bg-blue-700 transition-colors"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-300 ${isCollapsed ? '' : 'rotate-180'}`} />
        </button>
      </aside>

      {/* ── Mobile drawer ───────────────────────────────────────────── */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 flex flex-col w-64 bg-white border-r border-gray-200
          transition-transform duration-300 ease-in-out md:hidden
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <SidebarInner
          {...sharedProps}
          collapsed={false}
          isMobile
          onNavClick={() => setIsMobileOpen(false)}
          onClose={() => setIsMobileOpen(false)}
        />
      </aside>

      {/* ── Main content area ───────────────────────────────────────── */}
      <div className={`flex-1 flex flex-col min-w-0 ${location.pathname === '/batch-chat' ? 'min-h-0' : ''}`}>
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => setIsMobileOpen(true)}
            className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Open sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-sm font-semibold text-gray-700 flex-1">
            {isAdmin ? 'Admin Dashboard' : isEmployer ? 'Employer Dashboard' : 'Alumni Dashboard'}
          </span>
        </div>

        {/* Page content */}
        <main className={`flex-1 min-h-0 flex flex-col bg-gray-50 ${location.pathname === '/batch-chat' ? 'overflow-hidden' : 'overflow-y-auto p-4 md:p-6'}`}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default DashboardLayout
